/**
 * Cliente HTTP de la galería `/reportes.html` — Fase 5f.
 *
 * Único consumidor de `GET /api/mission-reports/` con todos los campos
 * extra (theater, durationSec, uploadedByCallsign, pilotCount). El
 * dropdown de briefing/debriefing usa el mismo endpoint pero solo lee
 * los campos legacy — los nuevos viajan en el mismo payload sin afectar
 * a esos consumidores.
 */

export interface MissionReportRow {
  id: string;
  reportNumber: string;
  /** Título técnico extraído del `.acmi`/`.miz` por el parser. */
  title: string | null;
  /** Campaña declarada a mano por el oficial al subir o editar. */
  declaredCampaign: string | null;
  /** Misión declarada a mano por el oficial. La galería la prioriza
   *  sobre `title` cuando está presente. */
  declaredMission: string | null;
  uploadedAt: string;
  missionDateIso: string | null;
  theater: string | null;
  durationSec: number | null;
  /** UUID del piloto que subió el reporte. NULL si su cuenta fue borrada. */
  uploadedByUserId: string | null;
  /** Callsign del piloto que subió. NULL si su cuenta fue borrada. */
  uploadedByCallsign: string | null;
  pilotCount: number;
  /**
   * Estado del pipeline. Con `includeUnparsed=1` llegan también los que
   * no están `parsed` (los propios; un admin+ ve los de todos).
   */
  status: ReportListStatus;
}

/** Estados posibles de un reporte en el listado. */
export type ReportListStatus = 'uploaded' | 'parsing' | 'parsed' | 'error';

/** Cliente DELETE — usado por el botón X de la galería y de /reporte.html. */
export async function deleteMissionReport(
  reportNumber: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  let res: Response;
  try {
    res = await fetch(`/api/mission-reports/${encodeURIComponent(reportNumber)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  } catch (err) {
    console.error('[reportes-list] DELETE network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (res.ok) return { ok: true };
  // Intenta leer mensaje del backend si lo hay.
  let message = `El servidor respondió con ${String(res.status)}.`;
  try {
    const body = (await res.json()) as { error?: string };
    if (typeof body.error === 'string') message = body.error;
  } catch {
    /* ignore */
  }
  return { ok: false, message };
}

interface MissionReportsResponse {
  missions: MissionReportRow[];
}

export type FetchMissionReportsResult =
  | { ok: true; data: MissionReportRow[] }
  | { ok: false; message: string };

/**
 * Lista de reportes para la galería.
 *
 * Pide `includeUnparsed=1` para que aparezcan también los reportes que
 * el pipeline no terminó (Fase 5h.3). El backend solo devuelve los del
 * propio usuario, salvo a un admin+, que los ve todos. Los desplegables
 * de briefing/debriefing NO pasan el parámetro y siguen viendo solo los
 * `parsed`.
 */
export async function fetchMissionReports(): Promise<FetchMissionReportsResult> {
  let res: Response;
  try {
    // Sin trailing slash: el router de Hono lo monta como `/api/mission-reports`
    // y el `/` final devuelve 404. Mismo criterio que `fetchBriefingMissions`.
    res = await fetch('/api/mission-reports?includeUnparsed=1', { credentials: 'include' });
  } catch (err) {
    console.error('[reportes-list] fetch network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) {
    return {
      ok: false,
      message: `El servidor respondió con ${String(res.status)}.`,
    };
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  if (
    typeof body !== 'object' ||
    body === null ||
    !Array.isArray((body as MissionReportsResponse).missions)
  ) {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  return { ok: true, data: (body as MissionReportsResponse).missions };
}
